import { useMemo, useState } from 'react';

import type { OptionGroup, Option } from '../types';

interface UseVisibleGroupOptionsReturn<O extends Option> {
  visibleOptionGroups: OptionGroup<O>[];
  showMore?: VoidFunction;
}

function useVisibleGroupOptions<O extends Option>({
  optionGroups,
  step = undefined,
}: {
  optionGroups: OptionGroup<O>[];
  step?: number;
}): UseVisibleGroupOptionsReturn<O> {
  const [visibleCount, setVisibleCount] = useState(step);

  const visibleOptionGroups = useMemo<OptionGroup<O>[]>(() => {
    if (visibleCount === undefined) return optionGroups;

    let shown = 0;
    return optionGroups.reduce<OptionGroup<O>[]>((acc, group) => {
      if (shown >= visibleCount) return acc;

      const remaining = visibleCount - shown;
      const visibleInGroup = (group).options?.slice(0, remaining) ?? [];
      shown += visibleInGroup.length;

      if (visibleInGroup.length > 0) {
        acc.push({
          ...group,
          options: visibleInGroup,
        } as typeof group);
      }

      return acc;
    }, []);
  }, [optionGroups, visibleCount]);

  const totalVisibleCount = optionGroups.reduce(
    (acc, group) => {
      return acc + Number(group.options?.length);
    },
    0,
  );

  let showMore;
  if (visibleCount !== undefined && step !== undefined && visibleCount < totalVisibleCount) {
    showMore = () => setVisibleCount(visibleCount + step);
  }

  return {
    visibleOptionGroups,
    showMore,
  };
}

export { useVisibleGroupOptions };
